import React, { useMemo } from "react";

export interface BeatCountInOverlayProps {
  /** Fin de la entrada (ms): tiempo cubierto por la cuenta (puede ser > primer ataque si hay anacrusa “vacía” en tiempo 0). */
  introEndMs: number;
  playbackMs: number;
  pulseMs: number;
  beatType: number;
  darkMode?: boolean;
  accent: string;
  /** Alineación horizontal con la columna del playhead (px desde la izquierda del padre). */
  anchorLeftPx: number;
  /** Primer compás es anacrusa (textos de apoyo si no hay `pickupTotalPulses`). */
  pickupLeadIn?: boolean;
  /**
   * Anacrusa: pulsos ascendentes 1…N; luego «¡» al final del último pulso.
   * Si es 0, cuenta regresiva clásica hasta `introEndMs`.
   */
  pickupTotalPulses?: number;
  /**
   * Compás sin anacrusa (2/4, 3/4, 4/4 con denominador 4): pulsos 1…N−1 y «Y» en el último.
   * Solo si `pickupTotalPulses` es 0.
   */
  standardMeasureBeatCount?: number;
}

/**
 * Cuenta de entrada: en anacrusa con pulsos definidos, 1…N + «¡»; si no, regresiva.
 */
export function BeatCountInOverlay({
  introEndMs,
  playbackMs,
  pulseMs,
  beatType,
  darkMode = false,
  accent,
  anchorLeftPx,
  pickupLeadIn = false,
  pickupTotalPulses = 0,
  standardMeasureBeatCount = 0,
}: BeatCountInOverlayProps) {
  const { visible, displayMain, beatKey, beatLabel, subLabel } = useMemo(() => {
    if (introEndMs <= 0 || pulseMs <= 0) {
      return { visible: false, displayMain: "", beatKey: 0, beatLabel: "", subLabel: "" };
    }
    const remaining = introEndMs - playbackMs;
    if (remaining <= 0 || playbackMs < 0) {
      return { visible: false, displayMain: "", beatKey: 0, beatLabel: "", subLabel: "" };
    }
    const minPulse =
      pickupTotalPulses > 0 ? pulseMs * 0.22 : pickupLeadIn ? pulseMs * 0.28 : pulseMs * 0.45;
    if (introEndMs < minPulse) {
      return { visible: false, displayMain: "", beatKey: 0, beatLabel: "", subLabel: "" };
    }

    const beatKey = Math.floor(playbackMs / pulseMs);

    if (pickupTotalPulses > 0) {
      const stepIdx = Math.min(pickupTotalPulses - 1, beatKey);
      const phase = (playbackMs % pulseMs) / pulseMs;
      const onLast = stepIdx >= pickupTotalPulses - 1;
      const showGo = onLast && phase >= 0.62;
      const num = Math.min(pickupTotalPulses, stepIdx + 1);
      const displayMain = showGo ? "¡" : String(num);
      return {
        visible: true,
        displayMain,
        beatKey,
        beatLabel: beatType === 8 ? "Entrada (corchea)" : "Entrada (pulso)",
        subLabel: `Anacrusa — ${pickupTotalPulses} pulsos hasta el golpe`,
      };
    }

    if (
      standardMeasureBeatCount >= 2 &&
      standardMeasureBeatCount <= 4 &&
      pickupTotalPulses <= 0
    ) {
      const stepIdx = Math.min(standardMeasureBeatCount - 1, beatKey);
      const displayMain =
        stepIdx >= standardMeasureBeatCount - 1 ? "Y" : String(stepIdx + 1);
      return {
        visible: true,
        displayMain,
        beatKey,
        beatLabel: beatType === 8 ? "Entrada (corchea)" : "Entrada (negra)",
        subLabel: `${standardMeasureBeatCount}/4 — hasta el primer tiempo`,
      };
    }

    const steps = Math.max(1, Math.ceil(introEndMs / pulseMs));
    const current = Math.max(1, Math.ceil(remaining / pulseMs));
    const capped = Math.min(current, steps);
    const beatLabel = beatType === 8 ? "Pulsos (corchea)" : "Pulsos (negra)";
    const subLabel = pickupLeadIn
      ? "Compás de entrada — hasta el primer ataque"
      : "Hasta el primer ataque";
    return { visible: true, displayMain: String(capped), beatKey, beatLabel, subLabel };
  }, [introEndMs, playbackMs, pulseMs, beatType, pickupLeadIn, pickupTotalPulses, standardMeasureBeatCount]);

  if (!visible) return null;

  return (
    <div
      className="pointer-events-none absolute z-30 flex flex-col items-center select-none"
      style={{
        left: anchorLeftPx,
        top: 10,
        transform: "translateX(-50%)",
        maxWidth: "min(92vw, 280px)",
      }}
    >
      <div
        key={beatKey}
        className="tabular-nums font-semibold leading-none"
        style={{
          color: darkMode ? accent : "#5a2d20",
          fontSize: "2rem",
          fontFamily: darkMode ? "'Inter', system-ui, sans-serif" : "'Playfair Display', Georgia, serif",
          textShadow: darkMode
            ? `0 0 18px rgba(201,64,90,0.45), 0 2px 12px rgba(0,0,0,0.55)`
            : "0 1px 0 rgba(255,255,255,0.9), 0 3px 14px rgba(90,45,32,0.2)",
          animation: "bordonaCountPop 0.22s ease-out",
        }}
      >
        {displayMain}
      </div>
      <div
        className="mt-1 text-[10px] tracking-wide uppercase"
        style={{
          color: darkMode ? "rgba(232,230,227,0.55)" : "rgba(90,80,72,0.72)",
          letterSpacing: "0.12em",
        }}
      >
        {beatLabel}
      </div>
      <div
        className="mt-1.5 px-2 text-center text-[9px] leading-snug"
        style={{
          color: darkMode ? "rgba(232,230,227,0.42)" : "rgba(90,80,72,0.62)",
          letterSpacing: "0.04em",
        }}
      >
        {subLabel}
      </div>
      <style>{`
        @keyframes bordonaCountPop {
          0% { transform: scale(0.72); opacity: 0.35; }
          70% { transform: scale(1.06); opacity: 1; }
          100% { transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
